<script lang="ts" setup>
import { useSlots, type VNode, type VNodeArrayChildren } from 'vue';
import AsteriskIcon from '../../svg/asterisk-icon.svg';

withDefaults(defineProps<{
    block?: boolean;
    compactLabel?: boolean;
    label?: string;
    description?: string|null;
    required?: boolean;
}>(), {
    block: false,
    // Narrows the label column from half the row to a third, letting the
    // control breathe. Classes stay static so Tailwind keeps them.
    compactLabel: false,
    required: false,
    description: undefined,
    label: ''
});

defineSlots<{
    default(): unknown;
    label(props: {id?: string}): unknown;
}>();

function findIdInVNodes(vnodeArray?: VNode[]|VNodeArrayChildren): string | undefined {
    if(!vnodeArray) {
        return;
    }
    for(const vnode of vnodeArray) {
        if(typeof vnode !== 'object' || !vnode) {
            continue;
        }
        if('props' in vnode && vnode.props && 'id' in vnode.props) {
            return vnode.props.id;
        }
        if('children' in vnode && Array.isArray(vnode.children)) {
            const id = findIdInVNodes(vnode.children);
            if(id) {
                return id;
            }
        }
    }

    return;
}

const slots = useSlots();
const id = findIdInVNodes(slots.default?.());
</script>

<template>
    <section
        class="flex flex-wrap border-b gap-y-2 last:border-none border-neutral-200 dark:border-neutral-700"
        :class="{ 'py-4': block, 'px-3 py-2': !block }">
        <div
            class="w-full"
            :class="{
                'lg:m-0 lg:pr-2': !block,
                'lg:w-1/2': !block && !compactLabel,
                'lg:w-1/3': !block && compactLabel,
            }">
            <div class="flex items-center gap-x-1">
                <slot
                    :id="id"
                    name="label">
                    <label
                        :for="id"
                        class="block font-medium text-base text-neutral-700 dark:text-neutral-200">{{ label }}</label>
                    <AsteriskIcon
                        v-if="required"
                        class="text-rose-600 h-[0.6rem] w-[0.6rem]" />
                </slot>
            </div>
            <p
                v-if="description"
                class="text-neutral-500 dark:text-neutral-400"
                :class="{ 'mb-2': !!block, 'w-5/6': !compactLabel, 'w-full': compactLabel }">
                {{ description }}
            </p>
        </div>
        <div
            class="flex w-full flex-col justify-center"
            :class="{
                'lg:w-1/2': !block && !compactLabel,
                'lg:w-2/3': !block && compactLabel,
            }">
            <div class="input-field is-empty form-group">
                <slot />
            </div>
        </div>
    </section>
</template>